import React, { useEffect, useState } from 'react';

import { makeStyles } from '@mui/styles';

import CompletedSummary from './CompletedSummary';
import ExpandedTitleRow from './ExpandedTitleRow';
import Info from './Info';
import ProgressBar from './ProgressBar';

const useStyles = makeStyles(() => ({
    container: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        width: '100%',
        padding: '32px 0 32px 0 !important'
    },
    divider: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '1px',
        backgroundColor: '#E3E3FA'
    }
}));

const possessiveName = (memberName?: string) => {
    if (!memberName) {
        return `This member's`;
    }

    return memberName.endsWith('s') || memberName.endsWith('S')
        ? `${memberName}'`
        : `${memberName}'s`;
};

type BaseProgressBarProps = {
    extraLimit: number;
    extraUsed?: number;
    extraYearlyDepleted?: boolean;
    limit: number;
    name: string;
    remaining?: number;
    samaritanYearlyDepleted?: boolean;
    used: number;
};

const BaseProgressBar: React.FC<BaseProgressBarProps> = ({
    extraLimit,
    extraUsed = 0,
    extraYearlyDepleted = false,
    limit,
    name,
    remaining: assignableRemaining,
    samaritanYearlyDepleted = false,
    used
}) => {
    const classes = useStyles();
    const month = new Date().toLocaleString('default', { month: 'long' });
    const title = `${month} Bonus Pool`;
    const remaining = limit - used;
    const extraRemaining = extraLimit - extraUsed;
    const isYearlyComplete = samaritanYearlyDepleted;
    const isCollapsable = !isYearlyComplete && remaining <= 0 && extraLimit > 0;
    const [collapsed, setCollapsed] = useState(true);
    const [yearlyCollapsed, setYearlyCollapsed] = useState(true);
    const isOpen = !(isCollapsable && collapsed);
    const showOpenDivider = isOpen && extraLimit > 0 && !isYearlyComplete;
    const yearlyMessage = `${possessiveName(name)} bonus funds for this year were fully assigned.`;
    useEffect(() => {
        if (isCollapsable) {
            setCollapsed(true);
        }
    }, [isCollapsable]);

    useEffect(() => {
        if (isYearlyComplete) {
            setYearlyCollapsed(true);
        }
    }, [isYearlyComplete]);

    const handleExpand = () => {
        setCollapsed(false);
    };

    const handleCollapse = () => {
        setCollapsed(true);
    };

    if (isYearlyComplete && yearlyCollapsed) {
        return (
            <CompletedSummary
                onExpand={() => setYearlyCollapsed(false)}
                subtitle={yearlyMessage}
                title={title}
            />
        );
    }

    if (isYearlyComplete) {
        const monthStillHasRoom = remaining > 0;

        return (
            <div className={classes.container}>
                <ExpandedTitleRow
                    collapsable
                    onCollapse={() => setYearlyCollapsed(true)}
                    title={title}
                />
                <ProgressBar
                    remaining={monthStillHasRoom ? 0 : undefined}
                    used={used}
                    limit={limit}
                />
                {monthStillHasRoom && <Info message={yearlyMessage} />}
                {extraLimit > 0 && <div className={classes.divider} />}
            </div>
        );
    }

    if (isCollapsable && collapsed) {
        return (
            <CompletedSummary
                onExpand={handleExpand}
                subtitle={`You've assigned all $${limit} from the base pool.${extraRemaining > 0 && !extraYearlyDepleted ? ` The extra bonuses are now available to use.` : ''}`}
                title={title}
            />
        );
    }

    return (
        <div className={classes.container}>
            {isCollapsable ? (
                <ExpandedTitleRow
                    collapsable
                    onCollapse={handleCollapse}
                    title={title}
                />
            ) : (
                <ExpandedTitleRow title={title} />
            )}
            <ProgressBar
                remaining={assignableRemaining}
                used={used}
                limit={limit}
            />
            {assignableRemaining != null &&
            assignableRemaining < remaining &&
            assignableRemaining > 0 ? (
                <Info
                    message={`$${assignableRemaining} remaining in ${possessiveName(name)} yearly bonus funds`}
                />
            ) : (
                !samaritanYearlyDepleted && (
                    <Info name={name} limit={limit} used={used} />
                )
            )}
            {showOpenDivider && <div className={classes.divider} />}
        </div>
    );
};

export default BaseProgressBar;
